import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Typography,
  Badge,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { IconCheck, IconX, IconLoader, IconClock } from "@tabler/icons-react";
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from "react-router-dom";
import { useEvaluationProgress } from "../hooks";

interface EvaluationProgressDialogProps {
  jobId: string | null;
  open: boolean;
  onClose: () => void;
}

export const EvaluationProgressDialog: React.FC<EvaluationProgressDialogProps> = ({
  jobId,
  open,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { jobStatus, progress, isLoading } = useEvaluationProgress({
    jobId,
    enabled: open,
    onComplete: () => {
      // Force immediate refetch of evalResults query
      queryClient.refetchQueries({ queryKey: ["evalResults"] });

      // Auto-close and navigate to "All Results" tab after 10 seconds
      setTimeout(() => {
        // Navigate to "All Results" tab to show fresh results
        navigate("?tab=results");

        // Close the dialog
        onClose();
      }, 10000);
    },
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "passed":
        return <IconCheck className="al-w-4 al-h-4 al-text-green-600" />;
      case "failed":
        return <IconX className="al-w-4 al-h-4 al-text-red-600" />;
      case "running":
        return <IconLoader className="al-w-4 al-h-4 al-text-blue-600 al-animate-spin" />;
      case "pending":
        return <IconClock className="al-w-4 al-h-4 al-text-gray-400" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "passed":
        return (
          <Badge className="!al-bg-green-100 !al-text-green-700 al-border al-border-green-200">
            Passed
          </Badge>
        );
      case "failed":
        return (
          <Badge className="!al-bg-red-100 !al-text-red-700 al-border al-border-red-200">
            Failed
          </Badge>
        );
      case "running":
        return (
          <Badge className="!al-bg-blue-100 !al-text-blue-700 al-border al-border-blue-200">
            Running
          </Badge>
        );
      case "pending":
        return (
          <Badge className="!al-bg-gray-100 !al-text-gray-700 al-border al-border-gray-200">
            Pending
          </Badge>
        );
      default:
        return null;
    }
  };

  if (isLoading || !jobStatus) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="al-max-w-2xl">
          <DialogHeader>
            <DialogTitle>Evaluation Progress</DialogTitle>
          </DialogHeader>
          <Stack direction="row" justify="center" align="center" className="al-py-8">
            <IconLoader className="al-w-8 al-h-8 al-animate-spin al-text-primary" />
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="al-max-w-2xl al-max-h-[80vh] al-overflow-hidden">
        <DialogHeader>
          <DialogTitle>Evaluation Progress</DialogTitle>
        </DialogHeader>

        <div className="al-flex-1 al-overflow-y-auto">
          {/* Overall Status */}
          <div className="al-mb-6">
            <Stack direction="row" justify="between" align="center" className="al-mb-2">
              <Typography variant="h4">
                Overall Status
              </Typography>
              {getStatusBadge(jobStatus.status)}
            </Stack>

            {/* Progress Bar */}
            {progress && (
              <div className="al-mb-2">
                <div className="al-w-full al-bg-gray-200 al-rounded-full al-h-2">
                  <div
                    className="al-bg-primary al-h-2 al-rounded-full al-transition-all al-duration-300"
                    style={{ width: `${progress.percentage}%` }}
                  />
                </div>
              </div>
            )}

            {/* Progress Stats */}
            {progress && (
              <Stack direction="row" gap="md" className="al-mt-2">
                <Typography size="sm" className="al-text-gray-600">
                  Total: <span className="al-font-semibold">{progress.total}</span>
                </Typography>
                <Typography size="sm" className="al-text-gray-600">
                  Completed: <span className="al-font-semibold">{progress.completed}</span>
                </Typography>
                <Typography size="sm" className="al-text-green-600">
                  Successful: <span className="al-font-semibold">{progress.successful}</span>
                </Typography>
                <Typography size="sm" className="al-text-red-600">
                  Failed: <span className="al-font-semibold">{progress.failed}</span>
                </Typography>
              </Stack>
            )}
          </div>

          {/* Test Cases List */}
          <div>
            <Typography variant="h4" className="al-mb-3">
              Test Cases ({jobStatus.test_statuses.length})
            </Typography>

            <div className="al-space-y-2">
              {jobStatus.test_statuses.map(testStatus => (
                <Stack
                  key={testStatus.test_case_id}
                  direction="row"
                  align="center"
                  justify="between"
                  className="al-p-3 al-border al-border-gray-200 al-rounded-md"
                >
                  <Stack direction="row" align="center" gap="sm" className="al-flex-1">
                    {getStatusIcon(testStatus.status)}
                    <Typography size="sm" className="al-flex-1">
                      {testStatus.test_case_name}
                    </Typography>
                  </Stack>

                  <Stack direction="row" align="center" gap="sm">
                    {testStatus.score !== null && testStatus.score !== undefined && (
                      <Badge
                        className={
                          Math.round(testStatus.score) >= 4
                            ? "!al-bg-green-100 !al-text-green-700 al-border al-border-green-200"
                            : Math.round(testStatus.score) >= 3
                              ? "!al-bg-yellow-100 !al-text-yellow-700 al-border al-border-yellow-200"
                              : "!al-bg-red-100 !al-text-red-700 al-border al-border-red-200"
                        }
                      >
                        {Math.round(testStatus.score)}/5
                      </Badge>
                    )}
                    {getStatusBadge(testStatus.status)}
                  </Stack>
                </Stack>
              ))}
            </div>
          </div>

          {/* Completion Message */}
          {(jobStatus.status === "completed" || jobStatus.status === "failed") && (
            <div className="al-mt-6 al-p-4 al-bg-gray-50 al-rounded-md">
              <Typography size="sm" className="al-text-gray-600">
                {jobStatus.status === "completed"
                  ? "✓ Evaluation completed successfully! Closing in 10 seconds..."
                  : "✗ Evaluation completed with errors. Review the results below."}
              </Typography>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
